/**
 * Generate a unique listing key that combines multiple identifiers
 * This ensures that each listing block on a page has a unique identifier
 * for URL parameters, even if they have the same Sanity _key
 *
 * @param sanityKey - The Sanity _key from the CMS block
 * @param type - The document type (e.g., 'aboutFoodWaste.blog')
 * @param taxonomy - The taxonomy type (e.g., 'aboutFoodWaste.blog.categories')
 * @param postsPerPage - The number of posts per page (optional, for additional uniqueness)
 * @returns A unique string identifier for the listing block
 */
export function generateUniqueListingKey(
  sanityKey: string | undefined,
  type: string | undefined,
  taxonomy: string | undefined,
  postsPerPage?: number,
): string {
  const baseKey = sanityKey || 'default';
  const documentType = type || 'unknown';
  const taxonomyType = taxonomy || 'no-taxonomy';

  // Create a hash-like string from the combination
  const combinedString = postsPerPage
    ? `${baseKey}_${documentType}_${taxonomyType}_${postsPerPage}`
    : `${baseKey}_${taxonomyType}`;

  // Simple hash function to create a shorter, consistent key
  let hash = 0;
  for (let i = 0; i < combinedString.length; i++) {
    const char = combinedString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return `listing_${Math.abs(hash).toString(36)}`;
}
